import { post } from 'utils/network';
import { isCreditCardExpired } from 'utils/dateTimes';
import { ApiRoute, ErrorMessage, PaymentService } from 'config';

import Braintree, { CreditCard } from 'services/Braintree';
import BillingAddress from 'models/BillingAddress';
import type IApiResponse from 'models/IApiResponse';

const parseBraintreeError = (error): string => {
  if (error?.fieldErrors?.[0]?.message) {
    return error.fieldErrors[0].message as string;
  }

  if (error?.fieldErrors?.[0]?.fieldErrors) {
    return parseBraintreeError(error?.fieldErrors[0]);
  }

  return '';
};

const addCreditCard = async (
  number: string,
  cvv: string,
  expirationMonth: string,
  expirationYear: string,
  setError: (error: string) => void,
  setRequestInProgress: (isRequestInProgress: boolean) => void,
  onSuccess: () => void,
  billingAddress?: BillingAddress,
) => {
  let data;
  setError('');
  setRequestInProgress(true);

  if (isCreditCardExpired(expirationMonth, expirationYear)) {
    setError('Your credit card is expired.');
    setRequestInProgress(false);
    return;
  }

  try {
    const creditCard: CreditCard = {
      number,
      cvv,
      expirationMonth,
      expirationYear,
    };

    creditCard.billingAddress = {
      // @ts-expect-error TODO: fix null safety
      streetAddress: billingAddress?.address,
      // @ts-expect-error TODO: fix null safety
      locality: billingAddress?.city,
      // @ts-expect-error TODO: fix null safety
      region: billingAddress?.state,
      // @ts-expect-error TODO: fix null safety
      postalCode: billingAddress?.zip,
    };

    data = await Braintree.addCreditCard(creditCard);
  } catch (err) {
    // Braintree errors are nested to different levels, we need to parse
    // it recursively.
    const errorMessage = parseBraintreeError(err.details.originalError);
    setError(errorMessage);
    setRequestInProgress(false);
  }

  if (data) {
    const creditCard = data.creditCards[0];

    const response = await post<IApiResponse>(ApiRoute.PAYMENT_METHOD, {
      user: {
        payment_method_attributes: {
          token: creditCard.nonce,
          cc_last_four: creditCard.details.lastFour,
          cc_brand: creditCard.details.cardType,
          cc_exp_month: creditCard.details.expirationMonth,
          cc_exp_year: creditCard.details.expirationYear,
          provider_name: PaymentService.CREDIT_CARD,
        },
      },
    });

    if (response.ok) {
      await onSuccess();
    } else {
      setError(response?.parsedBody?.details || ErrorMessage.UNKNOWN);
      setRequestInProgress(false);
    }
  }
};

function useAddCreditCard(): typeof addCreditCard {
  return addCreditCard;
}

export default useAddCreditCard;
